import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Client } from 'src/providers/client/client';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { DemoAccountService } from 'src/shared/services/demo-account.service';
import { AccountManagerService } from 'src/shared/services/account-manager.service';
import { PmDemoLandingService } from 'src/shared/services/pm-demo-landing.service';
import { PmDemoAccountManagerService } from 'src/shared/services/pm-demo-account-manager.service';
import { PmCreateAccountPhoneService } from 'src/shared/services/pm-create-account-phone.service';
import { PmScheduleCallSelectionService } from 'src/shared/services/pm-schedule-call-selection.service';
import { PmScheduleCallFirstService } from 'src/shared/services/pm-schedule-call-first.service';
import { SocialAuthService } from 'src/shared/services/social-auth.service';
import { Util } from 'src/shared/util/util';

import { validateEmail } from 'src/shared/validator/validateEmail';
import { parsePhoneNumber } from 'src/tidy-ui-components/components/form/input-international-phone/international-number.utils';

/**
 * In-app "Get Started" onboarding wizard opened from the demo banner via the right
 * side panel. Walks a demo guest through creating their real account (silently
 * leaving the shared demo account), meeting their account manager, and booking a setup
 * call. The personalized-projection goal then hands off to Tell Us About Your Listings.
 */
@Component({
  selector: 'get-started-flow',
  templateUrl: './get-started-flow.component.html',
  styleUrls: ['./get-started-flow.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class GetStartedFlowComponent implements OnInit, OnDestroy {

  private static readonly STATE_KEY = 'getStartedFlowState';

  // Setup-call scheduling links, by goal.
  private static readonly DEFAULT_PROJECTION_CALENDLY = 'https://calendly.com/d/d3gg-fnr-9kd/get-revenue-estimate';
  private static readonly DEFAULT_LEARN_CALENDLY = 'https://calendly.com/d/dtnf-pfg-9gz/learn-about-tidy';

  // 1 Create Account · 2 Meet Account Manager · 3 Schedule Call.
  currentStep: 1 | 2 | 3 = 1;

  // Step 2 — Go Live (sales): the goal the user picks (projection vs. learn) is recorded
  // account-level so the status page knows whether to show action items.
  goal: 'projection' | 'learn' | null = null;

  // Step 2 introduces the dedicated account manager who'll run the onboarding call.
  // Resolved from the customer's /me payload (falls back to TIDY's default until assigned).
  managerName = AccountManagerService.DEFAULT.name;
  managerPhoto = AccountManagerService.DEFAULT.photoUrl;

  // --- Meet Your Account Manager experiment (pm-demo-account-manager) ---
  // amVariant = the bucketed experiment variant; amDesign = which MYAM design renders.
  amVariant = 'control';
  amDesign: 'control' | 'salesy-v1' | 'salesy-v2' | 'salesy-v3' = 'control';

  // pm-create-account-phone experiment: 'show-phone' renders a required phone field next to email.
  phoneVariant = 'control';
  // pm-schedule-call-selection experiment: 'auto-learn' hides the goal radios + loads Learn Calendly.
  scheduleSelectionVariant = 'control';
  // pm-schedule-call-first experiment: 'schedule-call-first' re-orders the flow so the user
  // schedules a call (entering at step 2) before creating an account.
  scheduleCallFirstVariant = 'control';
  get isScheduleFirstMode(): boolean { return this.scheduleCallFirstVariant === 'schedule-call-first'; }
  // Captured (before the conversion wipes localStorage) so afterAccountCreated knows the user
  // already scheduled a call during the demo session and should mark the new real account.
  private didScheduleInDemo = false;

  // The international phone input emits the selected country code; we read the full number from
  // the form value at save time (parsePhoneNumber), so this just satisfies the output binding.
  onPhoneCountryCodeChange(_code: any): void { /* no-op */ }
  // Copy below is adapted from the tfront /rentals marketing page.
  // salesy-v1 — product benefits on Go Live-style black cards (icon on left).
  readonly amBenefits: { title: string; badge?: string; icon: string; body: string }[] = [
    { title: 'Cleaning & Maintenance', badge: 'THE HARD PART', icon: 'assets/svg/flash-outline.svg', body: 'The deepest technology stack built for cleaning and maintenance — 12 years in the making.' },
    { title: 'Revenue Optimization', icon: 'assets/svg/cash-outline.svg', body: 'More reservations. Higher revenue per guest. Lower operating costs.' },
    { title: 'Guest Experience', icon: 'assets/svg/star-outline.svg', body: 'Automate the guest touchpoints that drive 5-star reviews.' },
    { title: 'Risk Reduction', icon: 'assets/svg/shield-checkmark-outline.svg', body: 'Stay protected and stay legal — nothing slips through the cracks.' },
  ];
  // salesy-v2 — social proof + savings.
  readonly amStats: { value: string; label: string }[] = [
    { value: '1.5M', label: 'Units on TIDY' },
    { value: '100k+', label: 'Owners & PMs' },
    { value: '12+ yrs', label: 'In business' },
  ];
  readonly amSavings = {
    title: 'Traditional PMs charge 18–35%. TIDY charges 3.9%.',
    body: 'Everything included — cleaning, maintenance, revenue, and guests. Save up to 90% vs a traditional property manager.',
  };
  readonly amTestimonials: { quote: string; attribution: string }[] = [
    { quote: '“I never wanted to use a property manager because of the cost. TIDY saves me all the work at a fraction of the price.”', attribution: 'Sarah M. · Airbnb Host · 8 properties · Austin, TX' },
    { quote: '“I have 12 properties. I used to play phone tag with 3 people to get turnovers done — now I just use TIDY. Way easier.”', attribution: 'Mark J. · Property Manager' },
    { quote: '“TIDY is the easiest system I have ever worked with. Adding photos to my cleanings made a huge difference. Just fantastic!”', attribution: 'Margaret Z. · Airbnb Host' },
  ];
  // salesy-v3 — the setup-call agenda + profit guarantee. Framed as what happens on the
  // call so the cards make sense in context (the page's goal is to book that call).
  readonly amSteps: { num: string; title: string; body: string }[] = [
    { num: '1', title: 'Review your setup', body: 'We go over your properties, listings, and goals together.' },
    { num: '2', title: 'Build your plan', body: 'Pricing, cleaning, turnovers, and guest messaging — configured your way.' },
    { num: '3', title: 'Go live', body: 'TIDY runs everything for 3.9%. You keep full control.' },
  ];
  readonly amGuarantee = {
    badge: 'Our promise to you',
    title: 'Profit Increase Guarantee',
    body: 'Increase revenue up to 20% and pay up to 90% less than a traditional PM — or get up to one year free.',
  };

  private amDesignForVariant(variant: string): 'control' | 'salesy-v1' | 'salesy-v2' | 'salesy-v3' {
    return (variant === 'salesy-v1' || variant === 'salesy-v2' || variant === 'salesy-v3') ? variant : 'control';
  }

  // The new-rental booking gate must show an MYAM page even when the user's pm-demo-account-manager
  // variant is 'skip-to-schedule'. Pick one of the real MYAM designs at random for RENDERING ONLY —
  // never written to the sticky variant cache and never used for capture()/recordExposure(), so the
  // experiment's bucketing and funnel stay clean.
  private pickRandomMyamVariant(): 'control' | 'salesy-v1' | 'salesy-v2' | 'salesy-v3' {
    const designs: ('control' | 'salesy-v1' | 'salesy-v2' | 'salesy-v3')[] = ['control', 'salesy-v1', 'salesy-v2', 'salesy-v3'];
    return designs[Math.floor(Math.random() * designs.length)];
  }

  // Step 3 — what the setup call is for. Purely a UI choice until the call is
  // actually booked; a "projection" pick then hands off to Tell Us About Your
  // Listings, "learn" just finishes once the call is scheduled.
  readonly goalOptions: { value: 'projection' | 'learn'; viewValue: string; extraText: string }[] = [
    { value: 'projection', viewValue: 'Get Personalized Projection', extraText: 'We\'ll build a personalized financial projection from your listing and property.' },
    { value: 'learn', viewValue: 'Learn about TIDY', extraText: 'A walkthrough of how TIDY runs cleaning, maintenance, and your guest experience.' }
  ];

  // Step 1 — create account (sign up only)
  signUpForm: FormGroup;
  submitted = false;
  isCreatingAccount = false;
  accountError = '';

  // Optional entry params: `startStep` from the pill / status page; forceAccountManagerPage is
  // set by the new-rental booking gate (DemoBlockService) to force the Meet-Your-Account-Manager
  // page (step 2) even for skip-to-schedule users; `returnRoute` is where finishing goes. A
  // startStep of 4+ (the retired Add Listings wizard) hands off to Tell Us About Your Listings.
  data: { startStep?: number; addressId?: number; forceAccountManagerPage?: boolean; returnRoute?: string } | null = null;
  private returnRoute: string | null = null;

  // Step 6 — Calendly
  @ViewChild('calendlyContainer') calendlyContainer?: ElementRef;
  private calendlyScript: HTMLScriptElement | null = null;
  private calendlyObserver: MutationObserver | null = null;
  private boundCalendlyListener = this.onCalendlyMessage.bind(this);

  private fb = inject(FormBuilder);
  private client = inject(Client);
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private demoAccountService = inject(DemoAccountService);
  private accountManagerService = inject(AccountManagerService);
  private pmDemoLanding = inject(PmDemoLandingService);
  private amExperiment = inject(PmDemoAccountManagerService);
  private phoneExperiment = inject(PmCreateAccountPhoneService);
  private scheduleSelection = inject(PmScheduleCallSelectionService);
  private scheduleFirst = inject(PmScheduleCallFirstService);
  private socialAuth = inject(SocialAuthService);
  windowService = inject(WindowService);
  private storage = inject(TidyStorage);
  util = inject(Util);
  private cdr = inject(ChangeDetectorRef);

  // Mobile full-page chrome: tidy-top-nav title (the per-step header). Updated by
  // updateBackAction(). `ready` gates the body until entry params resolve (so a mobile
  // route doesn't briefly flash the wrong step before reading dialog-params).
  stepTitle = 'Create Account';
  ready = false;

  ngOnInit(): void {
    this.signUpForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, validateEmail]],
      password: ['', [Validators.required, Validators.minLength(8)]]
    });
    // pm-create-account-phone experiment: variant + schedule-selection variant were bucketed
    // at demo login, so read from cache. In the phone variant, add a required phone control.
    this.phoneVariant = this.phoneExperiment.getVariant();
    this.scheduleSelectionVariant = this.scheduleSelection.getVariant();
    this.scheduleCallFirstVariant = this.scheduleFirst.getVariant();
    if (this.phoneVariant === 'show-phone') {
      this.signUpForm.addControl('phone', this.fb.control('', Validators.required));
    }
    // Resolve the assigned account manager (name + photo shown on step 2) from /me. Re-run
    // with forceRefresh after account creation (afterAccountCreated), since ngOnInit runs
    // during the demo session — before the real account (and its manager) exists.
    this.loadAccountManager();
    // Warm the PM-demo experiment variants now (no exposure) so each branch is instant.
    // Exposure for all of these was already recorded at demo login.
    this.amExperiment.prefetchVariant();
    this.phoneExperiment.prefetchVariant();
    this.scheduleSelection.prefetchVariant();
    this.scheduleFirst.prefetchVariant();
    // Desktop dialog sets `data` directly; a mobile full-page route (navigateTo →
    // navigateForward) passes them via dialog-params storage. Support both.
    if (this.data) {
      this.applyEntry(this.data);
    } else {
      // Never leave applyEntry unrun — it's what flips `ready`, so a rejected read here
      // would render the flow permanently blank.
      this.storage.retrieve('dialog-params')
        .then((p: any) => this.applyEntry(p || {}))
        .catch(() => this.applyEntry({}));
    }
  }

  // Decide the starting step from entry params (startStep/addressId), then run the
  // appropriate setup and reveal the body.
  private applyEntry(params: { startStep?: number; addressId?: number; forceAccountManagerPage?: boolean; returnRoute?: string }): void {
    this.returnRoute = params?.returnRoute || null;
    // Keep the MYAM design consistent with the user's variant if Step 2 is shown directly
    // (e.g. a returning account re-opens the flow). Exposure is not fired here.
    this.amDesign = this.amDesignForVariant(this.amExperiment.getVariant());
    if (params?.startStep) {
      const start = params.startStep;
      if (start >= 4) {
        // The step-4 Add Listings wizard is retired: anything still asking for it (stale
        // dialog-params, an old deep link) lands on Tell Us About Your Listings instead.
        this.ready = true;
        this.openListingsSetup(params.addressId);
        return;
      }
      // Opened from the pill / status page; restore the chosen goal from metadata.
      this.currentStep = start as 1 | 2 | 3;
      this.hydrateGoalFromMetadata();
    } else if (this.demoAccountService.isDemoSession()) {
      // Demo flow: Create Account → Go Live (sales) → Schedule Call. A still-in-demo
      // guest always starts by creating their account.
      this.currentStep = 1;
    } else {
      // A real account re-opened the flow generically (e.g. dismissed before scheduling
      // a call) — land on the Go Live page that asks about a personalized projection.
      this.currentStep = 2;
    }
    // pm-create-account-phone experiment funnel entry: the create-account page (step 1) is
    // shown to a demo visitor. Capture here — not inside one branch — so it fires on BOTH
    // desktop (opens the flow with no startStep) and mobile (navigates with startStep:1).
    // It was previously only in the demo branch, so mobile never fired it and the primary
    // funnel was blind to every mobile user.
    if (this.currentStep === 1 && this.demoAccountService.isDemoSession()) {
      this.phoneExperiment.capture('viewed_create_account_page');
    }
    // pm-schedule-call-first: the salesy/MYAM page (step 2) is shown to a demo visitor BEFORE
    // any account exists, so fire the account-manager funnel's entry event here — it normally
    // fires in afterAccountCreated(), which this re-ordered flow bypasses. Before the
    // skip-to-schedule redirect so it fires for every AM variant, matching control.
    if (this.isScheduleFirstMode && this.demoAccountService.isDemoSession() && this.currentStep === 2) {
      this.amExperiment.capture('viewed_salesy_page');
    }
    // skip-to-schedule (pm-demo-account-manager) users never see the Meet-Your-Account-Manager
    // page. If we'd land them there — re-opening from the "Schedule Setup Call" pill (startStep
    // 2) or the generic real-account re-entry above — route straight to the schedule-call page
    // instead, the same way afterAccountCreated() does right after signup.
    // EXCEPTION: the new-rental booking gate (forceAccountManagerPage) requires the MYAM page to be
    // shown even to skip-to-schedule users, so render a random non-skip design instead of
    // redirecting. Render-only — the true cached variant is untouched, and no funnel/exposure event
    // fires here for a real account (viewed_salesy_page above is demo-session-gated).
    if (this.currentStep === 2 && this.amExperiment.getVariant() === 'skip-to-schedule') {
      if (params?.forceAccountManagerPage) {
        this.amDesign = this.amDesignForVariant(this.pickRandomMyamVariant());
      } else {
        this.ready = true;
        this.goToScheduleCall();
        return;
      }
    }
    this.updateBackAction();
    this.resumeStepSideEffects();
    this.ready = true;
    this.cdr.markForCheck();
  }

  private async hydrateGoalFromMetadata(): Promise<void> {
    if (this.goal) { return; }
    try {
      const state = await this.demoAccountService.getOnboardingState();
      if (state.goal) {
        this.goal = state.goal;
        this.cdr.markForCheck();
      }
    } catch {
      // Non-fatal — the goal alert / projection routing just won't be restored.
    }
  }

  ngOnDestroy(): void {
    this.rightSidePanelService.customBackAction$.next(null);
    this.rightSidePanelService.customSkipAction$.next(null);
    this.destroyCalendly();
  }

  private goToStep(step: 1 | 2 | 3): void {
    this.currentStep = step;
    this.persistFlowState();
    this.updateBackAction();
    this.cdr.markForCheck();
    if (step === 3) {
      setTimeout(() => this.loadInlineCalendly(), 0);
    }
  }

  /**
   * Persist the flow position so closing the dialog (e.g. to keep exploring)
   * and re-opening it from the banner resumes on the same step. Survives reloads
   * via localStorage — important because once the real account exists we must not
   * restart from the "create account" step.
   */
  private persistFlowState(): void {
    localStorage.setItem(GetStartedFlowComponent.STATE_KEY, JSON.stringify({ step: this.currentStep }));
  }

  private clearFlowState(): void {
    localStorage.removeItem(GetStartedFlowComponent.STATE_KEY);
  }

  private resumeStepSideEffects(): void {
    if (this.currentStep === 3) {
      setTimeout(() => this.loadInlineCalendly(), 0);
    }
  }

  // ---------- Step 2: Meet your account manager ----------

  // CTA on the account-manager page: go straight to the schedule-call page.
  // Default the goal to "projection" (the richer flow) so a radio is preselected
  // there; the user can still switch it before booking.
  goToScheduleCall = async () => {
    // Resolve the schedule-selection variant authoritatively before rendering step 3.
    // The synchronous getVariant() read in ngOnInit can return the 'control' default if
    // the sticky cache is cold (e.g. the brief window before flags load), which would
    // wrongly show the goal radios to an auto-learn user. resolveVariant() returns the
    // cached value instantly when warm, or the real flag value once flags are loaded.
    this.scheduleSelectionVariant = await this.scheduleSelection.resolveVariant();
    // pm-demo-account-manager experiment funnel: reached the schedule-call page.
    this.amExperiment.capture('viewed_scheduled_call');
    // pm-schedule-call-selection experiment funnel: reached the schedule-call page.
    this.scheduleSelection.capture('viewed_schedule_call_page');
    if (this.scheduleSelectionVariant === 'auto-learn') {
      // Variant: skip the goal radios and load the "Learn about TIDY" Calendly directly.
      this.goal = 'learn';
      this.demoAccountService.setOnboardingGoal('learn');
    } else if (!this.goal) {
      this.goal = 'projection';
      this.demoAccountService.setOnboardingGoal('projection');
    }
    this.goToStep(3);
  };

  // ---------- Step 3: what the setup call is for ----------

  // Record the goal account-level (metadata) and swap the Calendly embed to the
  // matching scheduling link. No navigation — the goal only takes effect after the
  // call is booked (projection → Add Property / Connect Listing).
  selectGoal(goal: 'projection' | 'learn'): void {
    if (this.goal === goal) { return; }
    this.goal = goal;
    this.demoAccountService.setOnboardingGoal(goal);
    if (this.currentStep === 3) {
      this.renderCalendly();
    }
  }

  // ---------- Step 1: Create account ----------

  createAccount = async () => {
    this.submitted = true;
    this.accountError = '';
    if (this.signUpForm.invalid) {
      this.cdr.markForCheck();
      return;
    }
    this.isCreatingAccount = true;
    this.cdr.markForCheck();
    try {
      // Read the schedule-call-first client flag BEFORE conversion wipes localStorage, so
      // afterAccountCreated() can mark the new real account as having scheduled a call.
      this.didScheduleInDemo = this.demoAccountService.hasScheduledInDemo();
      const accountType = localStorage.getItem('guest_view_type') || 'rental';
      await this.demoAccountService.convertDemoToRealAccount(this.signUpForm.value, accountType);
      // pm-create-account-phone experiment: persist the phone to the new account's profile via
      // the same endpoint the contact-info page uses (saveClientSettings → PUT customer/settings).
      if (this.phoneVariant === 'show-phone' && this.signUpForm.value.phone) {
        await this.client.saveClientSettings({ profile: { phone: parsePhoneNumber(this.signUpForm.value.phone) } })
          .catch(() => { /* don't block account creation on the phone save */ });
      }
      await this.afterAccountCreated();
    } catch (err: any) {
      this.accountError = err?.message || err?.error?.message || 'Failed to create account. Please try again.';
    } finally {
      this.isCreatingAccount = false;
      this.cdr.markForCheck();
    }
  };

  // After the account exists, branch on the Meet-Your-Account-Manager experiment
  // (pm-demo-account-manager). This is the funnel start, so record exposure here.
  private async afterAccountCreated(): Promise<void> {
    // The user was already enrolled (exposure fired at demo login); just read the cached
    // variant here — no exposure.
    const variant = await this.amExperiment.resolveVariant();
    this.amVariant = variant;
    this.amDesign = this.amDesignForVariant(variant);
    // pm-schedule-call-first: the account was created AFTER the user already scheduled a call in
    // the demo session. Fire the shared created_account KPI, mark the NOW-REAL account as having
    // scheduled (so it isn't re-prompted to schedule), and close the flow — skipping the MYAM /
    // schedule steps. viewed_salesy_page already fired at the step-2 entry (applyEntry), so it is
    // intentionally not re-fired here.
    if (this.isScheduleFirstMode && this.didScheduleInDemo) {
      this.phoneExperiment.capture('created_account');
      await this.demoAccountService.markSetupCallScheduled();
      this.demoAccountService.clearScheduledInDemo();
      this.completeFlow();
      return;
    }
    // Funnel step 1 — fires for EVERY variant (the renamed pm_am_exposure): they've reached
    // the post-account-creation salesy/account-manager step. The variant then decides what
    // they actually see, so the funnel can compare all six arms from this point.
    this.amExperiment.capture('viewed_salesy_page');
    // created_account KPI — the single canonical "account created" event, shared by both
    // the pm-create-account-phone and pm-demo-landing experiments (each attributes it via
    // its own exposure). Fires once here for every real account creation (email + social
    // is_new_user), so it must not also be fired from DemoAccountService.
    this.phoneExperiment.capture('created_account');
    // Honor the bucketed variant: skip-to-schedule jumps straight to the schedule-call
    // step, control/salesy show the matching MYAM (salesy) page.
    if (variant === 'skip-to-schedule') {
      this.goToScheduleCall();
      return;
    }
    // The real account now exists (with its auto-assigned manager); refresh so the step-2
    // card shows the real manager instead of the default resolved during the demo session.
    await this.loadAccountManager(true);
    this.goToStep(2);
  }

  // Resolve the assigned account manager for the step-2 card. forceRefresh bypasses the
  // cached /me (used post-conversion, when the cache is still the demo account's).
  private async loadAccountManager(forceRefresh = false): Promise<void> {
    const manager = await this.accountManagerService.getDisplay(forceRefresh);
    this.managerName = manager.name;
    this.managerPhoto = manager.photoUrl;
    this.cdr.markForCheck();
  }

  signInWithGoogle = () => this.handleSocialAuth('google');
  signInWithApple = () => this.handleSocialAuth('apple');

  private async handleSocialAuth(provider: 'google' | 'apple'): Promise<void> {
    this.isCreatingAccount = true;
    this.accountError = '';
    this.cdr.markForCheck();
    try {
      const authResult = provider === 'google'
        ? await this.socialAuth.signInWithGoogle()
        : await this.socialAuth.signInWithApple();
      const response = await this.socialAuth.authenticateWithBackend(authResult);
      // Read the schedule-call-first client flag BEFORE conversion wipes localStorage.
      this.didScheduleInDemo = this.demoAccountService.hasScheduledInDemo();
      await this.demoAccountService.convertDemoToRealAccountSocial(response);
      if (response.is_new_user) {
        await this.afterAccountCreated();
      } else {
        // Existing account signed in — drop into it rather than the new-account flow.
        window.location.reload();
      }
    } catch (error: any) {
      const message = error?.message || error?.error?.message || '';
      if (!(message.includes('cancel') || message.includes('popup_closed'))) {
        this.accountError = `${provider === 'google' ? 'Google' : 'Apple'} sign-in failed. Please try again.`;
      }
    } finally {
      this.isCreatingAccount = false;
      this.cdr.markForCheck();
    }
  }

  // ---------- Step 3: Calendly ----------

  // The scheduling link depends on the chosen goal.
  private getCalendlyUrl(): string {
    return this.goal === 'learn'
      ? GetStartedFlowComponent.DEFAULT_LEARN_CALENDLY
      : GetStartedFlowComponent.DEFAULT_PROJECTION_CALENDLY;
  }

  private loadInlineCalendly(): void {
    window.addEventListener('message', this.boundCalendlyListener);
    if ((window as any).Calendly) {
      this.renderCalendly();
      return;
    }
    if (!this.calendlyScript) {
      this.calendlyScript = document.createElement('script');
      this.calendlyScript.src = 'https://assets.calendly.com/assets/external/widget.js';
      this.calendlyScript.async = true;
      this.calendlyScript.onload = () => this.renderCalendly();
      document.head.appendChild(this.calendlyScript);
    } else {
      this.renderCalendly();
    }
  }

  // (Re)mount the inline widget with the URL for the current goal. Safe to call on
  // every goal change — clears the previous iframe first.
  private renderCalendly(): void {
    const container = this.calendlyContainer?.nativeElement;
    const calendly = (window as any).Calendly;
    if (!container || !calendly) { return; }
    this.calendlyObserver?.disconnect();
    container.innerHTML = '';
    calendly.initInlineWidget({ url: this.getCalendlyUrl(), parentElement: container });
    this.watchForCalendlyIframe();
  }

  private watchForCalendlyIframe(): void {
    const container = this.calendlyContainer?.nativeElement;
    if (!container) { return; }
    this.calendlyObserver = new MutationObserver(() => {
      const iframe = container.querySelector('iframe') as HTMLIFrameElement;
      if (iframe) {
        iframe.style.height = '100%';
        this.calendlyObserver?.disconnect();
      }
    });
    this.calendlyObserver.observe(container, { childList: true, subtree: true });
  }

  private destroyCalendly(): void {
    this.calendlyObserver?.disconnect();
    this.calendlyObserver = null;
    window.removeEventListener('message', this.boundCalendlyListener);
    if (this.calendlyScript) {
      document.head.removeChild(this.calendlyScript);
      this.calendlyScript = null;
    }
  }

  private onCalendlyMessage(event: MessageEvent): void {
    if (event.data?.event === 'calendly.event_scheduled') {
      // pm-demo-account-manager experiment KPI: successfully scheduled a setup call via the
      // Calendly widget. Fired here for a real booking and in skipScheduleCall() for the
      // QA skip; afterCallScheduled() stays event-free to avoid double-counting.
      this.amExperiment.capture('scheduled_call', { goal: this.goal });
      this.destroyCalendly();
      this.afterCallScheduled();
    }
  }

  private async afterCallScheduled(): Promise<void> {
    // pm-schedule-call-first: the call was booked while STILL in the shared demo session (no
    // real account yet). Do NOT write the "call scheduled" metadata — it would land on the
    // shared demos@tidy.com account. Record a client-only flag instead, then send the user to
    // the Create Account page; the real account gets marked in afterAccountCreated().
    if (this.isScheduleFirstMode && this.demoAccountService.isDemoSession()) {
      this.demoAccountService.markScheduledInDemo();
      this.goToStep(1);
      return;
    }
    // The call is booked before any property exists, so it's account-level — record it in
    // metadata so the pill / status page can show a "call scheduled" indicator. Await the
    // write so the pill (refreshed when this dialog closes) reads the updated state.
    await this.demoAccountService.markSetupCallScheduled();
    // Personalized projection needs the client's listings, so hand off to Tell Us About
    // Your Listings. "Learn more" is done once the call is booked.
    if (this.goal === 'projection') {
      this.openListingsSetup();
    } else {
      this.completeFlow();
    }
  }

  // Demo convenience (hold-the-X QA skip): pretend a call was scheduled and move on.
  // Fires the same pm-demo-account-manager KPI event as a real Calendly booking so the
  // funnel is fully testable end-to-end; `skipped: true` keeps QA skips distinguishable.
  skipScheduleCall = () => {
    this.amExperiment.capture('scheduled_call', { goal: this.goal, skipped: true });
    this.destroyCalendly();
    this.afterCallScheduled();
  };

  // ---------- Completion ----------

  private completeFlow(): void {
    this.clearFlowState();
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      // Mobile full-page route — return to the page that opened the flow (the More page
      // unless the caller asked for somewhere else) rather than closing a dialog.
      this.navCtrl.navigateForward(this.returnRoute || 'more');
    }
  }

  // Hand off to Tell Us About Your Listings (every property × channel on one page), which
  // replaced the step-4 wizard. Resolved from the route registry rather than an import —
  // this component is declared in SharedModule and so is that one, and the panel is
  // already open here, so navigateTo swaps within it (full-page route on mobile).
  private openListingsSetup(addressId?: number): void {
    this.clearFlowState();
    this.rightSidePanelService.setDialogPageTitle('Tell Us About Your Listings');
    this.rightSidePanelService.navigateTo('listings-setup', { addressId, returnRoute: this.returnRoute || undefined });
  }

  // ---------- Shared helpers ----------

  // Per-step dialog header.
  private getStepTitle(): string {
    switch (this.currentStep) {
      case 2: return 'Schedule Setup Call';
      case 3: return 'Schedule Setup Call';
      default: return 'Create Account';
    }
  }

  private updateBackAction(): void {
    this.stepTitle = this.getStepTitle();
    this.rightSidePanelService.setDialogPageTitle(this.stepTitle);
    let back: (() => void) | null = null;
    if (this.currentStep === 3) {
      // Schedule-call page → back to the account-manager page; keep the chosen
      // goal so the radio selection persists if the user returns. Exception: the
      // skip-to-schedule (pm-demo-account-manager) variant never shows the
      // account-manager page (step 2) — the user lands here straight from account
      // creation — so backing into step 2 would drop them on a page they never saw.
      // Leave the in-flow back unset for that variant so the schedule-call page acts
      // as the flow's first page: back exits the flow and returns to the page that
      // opened it (mobile pops the nav stack; desktop hides the back arrow).
      if (this.amExperiment.getVariant() !== 'skip-to-schedule') {
        back = () => { this.destroyCalendly(); this.goToStep(2); };
      }
    }
    this.rightSidePanelService.customBackAction$.next(back);
    // QA-only: on the Schedule Setup Call step, holding the X for 5s reveals "Skip",
    // which completes the step using whichever goal radio is selected (skipScheduleCall
    // marks the call booked, then projection → wizard / learn → done).
    this.rightSidePanelService.customSkipAction$.next(
      this.currentStep === 3 ? () => this.skipScheduleCall() : null,
    );
  }

  // Mobile top-nav back button. When there's an in-flow back (Schedule Call → Account
  // Manager), perform it and return false so the top-nav does NOT
  // also navigate. Otherwise return true so the top-nav pops the nav stack back to whatever
  // page opened the flow (the Overview demo bar, or the More page post-conversion).
  // (tidy-top-nav: customBackAction returning false suppresses its own navigation.)
  handleTopNavBack = (): boolean => {
    const back = this.rightSidePanelService.customBackAction$.getValue();
    if (back) {
      back();
      return false;
    }
    return true;
  };

}
